import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ShieldAlert } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg || JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function ChangePassword() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const forced = !!user?.must_change_password;
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setBusy(true);
    try {
      const payload = { new_password: newPassword };
      if (!forced) payload.current_password = currentPassword;
      await api.post("/auth/change-password", payload);
      toast.success("Password updated");
      await refreshMe();
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-8 py-16" data-testid="change-password-page">
      <p className="text-xs uppercase tracking-[0.3em] text-primary font-bold">Account</p>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2 mb-2">
        {forced ? "Set your password" : "Change password"}
      </h1>
      {forced && (
        <div className="border border-destructive/60 bg-destructive/5 p-3 mb-6 flex items-start gap-2" style={{ borderRadius: 2 }}>
          <ShieldAlert className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-xs text-destructive uppercase tracking-[0.15em]">
            Your account was created by an admin. Please choose a new password before continuing.
          </p>
        </div>
      )}
      {!forced && (
        <p className="text-sm text-muted-foreground mb-8">Choose a strong password (at least 6 characters).</p>
      )}
      <form onSubmit={submit} className="space-y-5">
        {!forced && (
          <Field label="Current password" type="password" value={currentPassword} onChange={setCurrentPassword} testId="current-password" required />
        )}
        <Field label="New password" type="password" value={newPassword} onChange={setNewPassword} testId="new-password" required />
        <Field label="Confirm new password" type="password" value={confirm} onChange={setConfirm} testId="confirm-password" required />

        <button
          type="submit"
          disabled={busy}
          data-testid="change-password-submit-btn"
          className="tactical-btn w-full bg-primary text-primary-foreground hover:bg-[#C9E620] disabled:opacity-50 px-6 py-4 inline-flex items-center justify-center gap-2 text-sm"
        >
          {busy ? "Saving..." : "Save Password"} <ArrowRight className="w-4 h-4" strokeWidth={3} />
        </button>
      </form>
    </div>
  );
}

function Field({ label, type, value, onChange, testId, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        required={required}
        className="w-full bg-transparent border border-border focus:border-primary focus:outline-none px-4 py-3 mt-2 font-mono text-sm"
      />
    </div>
  );
}
